import { computed, inject, Injectable, signal } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { StatusBar, Style } from '@capacitor/status-bar';
import { ScreenResizeService } from '../screen.resize/screen-resize.service';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';

export type CurrentTheme = 'light' | 'dark' | 'default';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  currentTheme = signal<CurrentTheme>('default');
  isDefaultTheme = signal<boolean>(true);

  private storage = inject(StorageService);
  private colorScheme: MediaQueryList;
  private _screen = inject(ScreenResizeService);
  private isMobile = computed(() => this._screen.isNative());

  constructor() {
    this.colorScheme = window.matchMedia('(prefers-color-scheme: dark)');
  }

  async initTheme() {
    const storedTheme = (await this.storage.getItem('theme')) as CurrentTheme | null;

    if (storedTheme && storedTheme !== 'default') {
      this.isDefaultTheme.set(false);
      this.updateTheme(storedTheme);
    } else {
      this.isDefaultTheme.set(true);
      this.updateTheme(this.colorScheme.matches ? 'dark' : 'light');
      this.colorScheme.addEventListener('change', this.onDeviceThemeChange);
    }

    if (storedTheme && storedTheme !== 'default') {
      this.isDefaultTheme.set(false);
      this.updateTheme(storedTheme);
    } else {
      this.isDefaultTheme.set(true);
      this.updateTheme(this.colorScheme.matches ? 'dark' : 'light');
      this.colorScheme.addEventListener('change', this.onDeviceThemeChange);
    }
  }

  private async updateTheme(theme: CurrentTheme) {
    this.currentTheme.set(theme);

    const isDark = theme === 'dark';
    document.documentElement.classList.toggle('ion-palette-dark', isDark);

    if (!this.isMobile()) return;

    const backgroundColor = isDark ? '#222428' : '#168e4d';

    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: backgroundColor });
    await EdgeToEdge.setBackgroundColor({ color: backgroundColor });
  }

  private onDeviceThemeChange = (event: MediaQueryListEvent): void => {
    if (this.isDefaultTheme()) {
      this.updateTheme(event.matches ? 'dark' : 'light');
    }
  };

  setDarkTheme(): void {
    this.isDefaultTheme.set(false);
    this.storage.setItem('theme', 'dark');
    this.colorScheme.removeEventListener('change', this.onDeviceThemeChange);
    this.updateTheme('dark');
  }

  setLightTheme(): void {
    this.isDefaultTheme.set(false);
    this.storage.setItem('theme', 'light');
    this.colorScheme.removeEventListener('change', this.onDeviceThemeChange);
    this.updateTheme('light');
  }

  useDeviceTheme(): void {
    this.isDefaultTheme.set(true);
    this.storage.setItem('theme', 'default');
    this.updateTheme(this.colorScheme.matches ? 'dark' : 'light');
    this.colorScheme.addEventListener('change', this.onDeviceThemeChange);
  }
}
